import React from "react";
import {
  Box,
  Stack,
  Typography,
} from "@mui/material";
import CardBase from "../../components/global/CardBase";
import dayjs from "dayjs";
import SelectBase, { SelectOption } from "../../components/global/SelectBase";
import DatePickerBase from "../../components/global/DatePickerBase";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import BarChartNewTalentCompany from "./BarChartNewTalentCompany";
import { toast } from "react-toastify";

const periodOptions: SelectOption[] = [
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "other", label: "Custom Dates" },
];

const DailyNewUserCard = () => {
  const [talentCompanyPeriod, setTalentCompanyPeriod] =
    React.useState<SelectOption | null>(
      periodOptions.find((opt) => opt.value === "this-year") || null
    );

  const [talentCompanyPeriodDate, setTalentCompanyPeriodDate] = React.useState({
    startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
  });

  // Toggle untuk menggunakan dummy data (untuk testing dengan banyak data points)
  const [useDummyData, setUseDummyData] = React.useState(false);

  const {
    handleSubmit,
    control,
    clearErrors,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!dayjs(payload.start_date).isSame(dayjs(payload.end_date))) {
        if (dayjs(payload.start_date).isAfter(dayjs(payload.end_date))) {
          setError("end_date", { type: "custom", message: "Invalid" });
          return;
        }
      }
      setTalentCompanyPeriodDate({
        startDate: dayjs(payload.start_date).format("YYYY-MM-DD"),
        endDate: dayjs(payload.end_date).format("YYYY-MM-DD"),
      });
    } catch (err: any) {
      toast.error(err);
    }
  };

  React.useEffect(() => {
    setValue("start_date", dayjs().add(-1, "day"));
    setValue("end_date", dayjs());
    clearErrors();
  }, [talentCompanyPeriod, setValue, clearErrors]);

  return (
    <CardBase
      title="New Talents & Clients"
      sx={{ height: "100%" }}
      useGlassCard={false}
      headerActions={
        <Box sx={{ minWidth: 120, mt: 0 }}>
          <form key="talent-company-stat" onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Toggle untuk dummy data - hanya untuk development/testing */}
              {/* {process.env.NODE_ENV === "development" && (
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    mr: 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={useDummyData}
                    onChange={(e) => setUseDummyData(e.target.checked)}
                    style={{ marginRight: "4px" }}
                  />
                  Dummy Data
                </Box>
              )} */}
              <SelectBase
                options={periodOptions}
                value={talentCompanyPeriod}
                placeholder="Period"
                onChange={(selectedValue) => {
                  const option = selectedValue as SelectOption | null;
                  if (!option) return;

                  const value = option.value as string;
                  if (value === "this-week") {
                    setTalentCompanyPeriodDate({
                      startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("week").format("YYYY-MM-DD"),
                    });
                  } else if (value === "this-month") {
                    setTalentCompanyPeriodDate({
                      startDate: dayjs()
                        .startOf("month")
                        .format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
                    });
                  } else if (value === "this-year") {
                    setTalentCompanyPeriodDate({
                      startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
                    });
                  }
                  setTalentCompanyPeriod(option);
                }}
                sx={{ minWidth: 150, mt: 0 }}
              />
              {talentCompanyPeriod?.value === "other" && (
                <>
                  <Controller
                    name="start_date"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <DatePickerBase
                        label="Start Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => onChange(newValue)}
                        error={!!errors.start_date}
                        helperText={
                          !!errors.start_date && "This field is required"
                        }
                        sx={{ mt: 1, width: "75%" }}
                        required
                      />
                    )}
                  />
                  <Controller
                    name="end_date"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <DatePickerBase
                        label="End Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => onChange(newValue)}
                        error={!!errors.end_date}
                        helperText={
                          !!errors.end_date &&
                          "The end date must be a date after or equal to start date"
                        }
                        sx={{ mt: 1, width: "75%" }}
                        required
                      />
                    )}
                  />
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    sx={{ mt: 1, height: "100%" }}
                  >
                    Update
                  </LoadingButton>
                </>
              )}
            </Stack>
          </form>
        </Box>
      }
    >
      <BarChartNewTalentCompany
        id="chart-talent-company"
        params={talentCompanyPeriodDate}
        useDummyData={useDummyData}
      />
    </CardBase>
  );
};
export default DailyNewUserCard;
